import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, DollarSign, Award, BookOpen, Clock, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';
import { SEO } from './SEO';
import { buildBreadcrumb, buildInstitutionSchema } from '../lib/seo-schemas';

const institutionData = {
  'granville-college': {
    name: 'Granville College',
    location: 'Vancouver & Surrey, BC',
    established: '1993',
    campuses: [
      'Suite 600 & 700 - 549 Howe Street, Vancouver, BC',
      '13402-104 Avenue, 2nd Floor, Surrey, BC'
    ],
    description: 'Established in 1993 as Canada\'s first Veterinary Technician Assistant College, Granville College has expanded to offer comprehensive diploma programs in Healthcare, Business, Hospitality, and Legal fields.',
    specializations: ['Healthcare', 'Business', 'Hospitality', 'Legal', 'Veterinary'],
    image: 'https://images.unsplash.com/photo-1724502555813-9ea4fe5d9c1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb2xsZWdlJTIwYnVpbGRpbmclMjBjYW1wdXMlMjBDYW5hZGF8ZW58MHx8fHwxNzc3NTkxMzk0fDA&ixlib=rb-4.1.0&q=85',
    programs: [
      {
        name: 'Veterinary Technician Assistant',
        duration: '36 weeks',
        tuition: 'Domestic: $6,500 | International: $12,500',
        description: 'Hands-on training in veterinary care, animal handling, and clinical procedures',
        careers: ['Veterinary Assistant', 'Animal Care Technician', 'Veterinary Clinic Support']
      },
      {
        name: 'Healthcare Office Administration',
        duration: '43 weeks',
        tuition: 'Domestic: $7,000 | International: $13,000',
        description: 'Comprehensive training in medical office procedures, patient care coordination, and healthcare systems',
        careers: ['Medical Office Administrator', 'Healthcare Coordinator', 'Patient Services Representative']
      },
      {
        name: 'Accounting & Payroll Administration',
        duration: '43 weeks',
        tuition: 'Domestic: $6,800 | International: $12,500',
        description: 'Complete training in accounting principles, payroll processing, and financial management',
        careers: ['Accounting Technician', 'Payroll Administrator', 'Bookkeeper']
      },
      {
        name: 'Global Supply Chain Management',
        duration: '48 weeks',
        tuition: 'Domestic: $7,500 | International: $14,000',
        description: 'Learn logistics, procurement, inventory management, and supply chain optimization',
        careers: ['Supply Chain Coordinator', 'Logistics Specialist', 'Procurement Officer']
      },
      {
        name: 'Hospitality Management',
        duration: '40 weeks',
        tuition: 'Domestic: $6,200 | International: $11,500',
        description: 'Training in hotel operations, customer service, and hospitality industry management',
        careers: ['Hotel Manager', 'Front Office Supervisor', 'Hospitality Coordinator']
      },
      {
        name: 'Paralegal Studies',
        duration: '45 weeks',
        tuition: 'Domestic: $7,200 | International: $13,500',
        description: 'Learn legal procedures, documentation, research, and litigation support',
        careers: ['Paralegal', 'Legal Assistant', 'Court Clerk']
      }
    ],
    highlights: [
      'Small class sizes for personalized attention',
      'Flexible learning options (full-time, part-time, online)',
      'Job placement assistance and career support',
      'Experienced instructors with industry backgrounds',
      'Modern facilities and hands-on training',
      'Established partnerships with local employers'
    ]
  },
  'cambria-college': {
    name: 'Cambria College',
    location: 'British Columbia (Multiple Campuses)',
    established: '2010',
    campuses: [
      'Victoria Campus',
      'Vancouver Campus',
      'Kelowna Campus',
      'Surrey Campus'
    ],
    description: 'Cambria College offers career-focused diploma and certificate programs in healthcare, business, hospitality, and IT with a focus on practical skills and immediate employment.',
    specializations: ['Healthcare', 'Business', 'Hospitality', 'IT & Technology'],
    image: 'https://images.unsplash.com/photo-1595760867694-fd5132a92955?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb2xsZWdlJTIwYnVpbGRpbmclMjBjYW1wdXMlMjBDYW5hZGF8ZW58MHx8fHwxNzc3NTkxMzk1fDA&ixlib=rb-4.1.0&q=85',
    programs: [
      {
        name: 'Health Care Assistant',
        duration: '28 weeks',
        tuition: 'Domestic: $6,800 | International: $13,000',
        description: 'Train to provide compassionate care to elderly and ill patients in various healthcare settings',
        careers: ['Health Care Assistant', 'Personal Support Worker', 'Care Aide']
      },
      {
        name: 'Medical Office Assistant',
        duration: '30 weeks',
        tuition: 'Domestic: $6,500 | International: $12,500',
        description: 'Comprehensive training in medical office procedures, patient scheduling, and healthcare administration',
        careers: ['Medical Office Assistant', 'Medical Receptionist', 'Healthcare Administrator']
      },
      {
        name: 'Dental Office Administrator',
        duration: '32 weeks',
        tuition: 'Domestic: $6,300 | International: $12,000',
        description: 'Specialize in dental office management, patient care, and dental software systems',
        careers: ['Dental Office Administrator', 'Dental Receptionist', 'Dental Practice Coordinator']
      },
      {
        name: 'Digital Business Management',
        duration: '40 weeks (+40 week co-op)',
        tuition: 'Domestic: $9,000 | International: $17,000',
        description: 'Learn digital marketing, business operations, and online campaign management',
        careers: ['Digital Marketing Specialist', 'Business Coordinator', 'Social Media Manager']
      },
      {
        name: 'Hospitality Management',
        duration: '48 weeks (+48 week co-op)',
        tuition: 'Domestic: $7,800 | International: $14,500',
        description: 'Comprehensive training in hotel operations, food & beverage, and customer service excellence',
        careers: ['Hotel Manager', 'Restaurant Manager', 'Event Coordinator']
      },
      {
        name: 'Network Support Administrator',
        duration: '36 weeks',
        tuition: 'Domestic: $8,000 | International: $15,000',
        description: 'IT training in network management, Windows servers, and technical support',
        careers: ['Network Administrator', 'IT Support Specialist', 'Systems Administrator']
      },
      {
        name: 'Mental Health Support Worker',
        duration: '35 weeks',
        tuition: 'Domestic: $7,000 | International: $13,000',
        description: 'Training to support individuals with mental health challenges in various community settings',
        careers: ['Mental Health Worker', 'Community Support Worker', 'Crisis Intervention Specialist']
      }
    ],
    highlights: [
      'Four campuses across British Columbia',
      'Co-op placement opportunities available',
      'Up to $3,000 scholarships for eligible students',
      'Online and hybrid learning options',
      'Credit transfer opportunities to University Canada West',
      'Practicum placements for real-world experience'
    ]
  },
  'vcct': {
    name: 'VCCT (Vancouver College of Counsellor Training)',
    location: 'Vancouver, BC',
    established: '1995',
    campuses: [
      'Vancouver Downtown Campus',
      'Distance Education (Online Programs)'
    ],
    description: 'VCCT specializes in counselling and mental health training, offering diploma and certificate programs for those pursuing careers in counselling, therapy, and mental health support.',
    specializations: ['Counselling', 'Mental Health', 'Family Support', 'Addictions'],
    image: 'https://images.unsplash.com/photo-1769209435699-9cc1fd8e7f57?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHx1bml2ZXJzaXR5JTIwY2FtcHVzJTIwQnJpdGlzaCUyMENvbHVtYmlhJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzc3NTkxMzk1fDA&ixlib=rb-4.1.0&q=85',
    programs: [
      {
        name: 'Diploma of Professional Counselling',
        duration: '52 weeks',
        tuition: 'Domestic: $8,800 | International: $16,500',
        description: 'Comprehensive counselling training covering personal development, mental health, family support, and substance abuse',
        careers: ['Professional Counsellor', 'Mental Health Counsellor', 'Private Practice Therapist']
      },
      {
        name: 'Counselling for Intimacy in Relationships',
        duration: '21 weeks',
        tuition: 'Domestic: $3,500 | International: $6,500',
        description: 'Specialized training in couples counselling and relationship therapy',
        careers: ['Relationship Counsellor', 'Couples Therapist', 'Marriage Counsellor']
      },
      {
        name: 'Addictions Worker Certificate',
        duration: '24 weeks',
        tuition: 'Domestic: $3,800 | International: $7,000',
        description: 'Training in addiction support, recovery guidance, and substance abuse counselling',
        careers: ['Addictions Counsellor', 'Recovery Support Worker', 'Substance Abuse Specialist']
      },
      {
        name: 'Youth & Family Support Worker',
        duration: '24 weeks',
        tuition: 'Domestic: $3,800 | International: $7,000',
        description: 'Training to support youth and families in various community and social service settings',
        careers: ['Youth Counsellor', 'Family Support Worker', 'Child & Youth Care Worker']
      },
      {
        name: 'Career Exploration & Development Skills',
        duration: '18 weeks',
        tuition: 'Domestic: $3,000 | International: $5,500',
        description: 'Training in career counselling, assessment, and employment support services',
        careers: ['Career Counsellor', 'Employment Advisor', 'Career Development Practitioner']
      }
    ],
    highlights: [
      'On-campus and distance education options',
      'Programs taught by experienced counsellors and therapists',
      'Focus on ethical standards and real-world application',
      'Flexible for working professionals',
      'Certificates awarded per course completed',
      'Strong foundation for private practice or agency work'
    ]
  },
  'acc': {
    name: 'Assiniboine Community College (ACC)',
    location: 'Brandon & Winnipeg, MB',
    established: '1961',
    campuses: [
      'Victoria Avenue East Campus, Brandon, MB',
      'North Hill Campus, Brandon, MB',
      'Parkland Campus, Dauphin, MB',
      'Winnipeg Campus, Winnipeg, MB'
    ],
    description: 'With over 55 years of excellence, ACC offers 50+ full-time programs in Agriculture, Business, Culinary Arts, Health, and Trades with a "learn by doing" philosophy.',
    specializations: ['Agriculture & Environment', 'Business', 'Culinary Arts', 'Health Sciences', 'Trades'],
    image: 'https://images.unsplash.com/photo-1753246786395-b2eb9dd447fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxDYW5hZGlhbiUyMGNvbGxlZ2UlMjBjYW1wdXMlMjBidWlsZGluZyUyMG1vZGVybnxlbnwwfHx8fDE3NzU1MDE1NzF8MA&ixlib=rb-4.1.0&q=85',
    programs: [
      {
        name: 'Culinary Arts',
        duration: '2 years (diploma)',
        tuition: 'Domestic: $8,000/year | International: $15,000/year',
        description: 'State-of-the-art kitchen training in cooking techniques, menu planning, and kitchen management',
        careers: ['Chef', 'Sous Chef', 'Restaurant Manager', 'Catering Manager']
      },
      {
        name: 'Business Administration',
        duration: '2 years (diploma)',
        tuition: 'Domestic: $7,200/year | International: $13,500/year',
        description: 'Comprehensive business training in management, marketing, accounting, and entrepreneurship',
        careers: ['Business Manager', 'Marketing Coordinator', 'Operations Manager']
      },
      {
        name: 'Accounting',
        duration: '2 years (advanced diploma)',
        tuition: 'Domestic: $7,500/year | International: $14,000/year',
        description: 'In-depth accounting training with co-op placement opportunities',
        careers: ['Accountant', 'Financial Analyst', 'Accounting Manager']
      },
      {
        name: 'Practical Nursing',
        duration: '2 years (diploma)',
        tuition: 'Domestic: $8,500/year | International: $16,000/year',
        description: 'Nursing training with interactive HOLO suite technology and clinical placements',
        careers: ['Licensed Practical Nurse', 'Healthcare Provider', 'Clinical Nurse']
      },
      {
        name: 'Agriculture & Environmental Management',
        duration: '2 years (diploma)',
        tuition: 'Domestic: $7,800/year | International: $14,500/year',
        description: 'Hands-on training in sustainable agriculture, greenhouse management, and environmental science',
        careers: ['Farm Manager', 'Agricultural Consultant', 'Environmental Technician']
      },
      {
        name: 'Electrical Technician',
        duration: '2 years (diploma)',
        tuition: 'Domestic: $8,300/year | International: $15,500/year',
        description: 'Comprehensive electrical training in residential, commercial, and industrial systems',
        careers: ['Electrician', 'Electrical Technician', 'Maintenance Electrician']
      },
      {
        name: 'Hospitality & Tourism Management',
        duration: '2 years (diploma)',
        tuition: 'Domestic: $7,000/year | International: $13,000/year',
        description: 'Training in hotel management, event planning, and tourism industry operations',
        careers: ['Hotel Manager', 'Event Planner', 'Tourism Coordinator']
      },
      {
        name: 'Carpentry',
        duration: '1 year (certificate)',
        tuition: 'Domestic: $6,500 | International: $12,000',
        description: 'Hands-on carpentry and construction training with industry-standard tools',
        careers: ['Carpenter', 'Construction Worker', 'Renovation Specialist']
      }
    ],
    highlights: [
      'Over 55 years of educational excellence',
      '2+2 program: Combine 2 years at ACC + 2 years at university',
      'State-of-the-art facilities including teaching kitchen and greenhouse',
      'Strong employer partnerships across Manitoba',
      'Apprenticeship programming available',
      'Learn by doing philosophy with hands-on labs'
    ]
  },
  'swenam-college': {
    name: 'Swenam College',
    location: 'British Columbia',
    established: '2000',
    campuses: [
      'Main Campus, British Columbia'
    ],
    description: 'Swenam College provides research and career training in Business, Sciences, Psychology, and Nutrition with a focus on academic excellence and practical application.',
    specializations: ['Business', 'Sciences', 'Psychology', 'Nutrition', 'IT'],
    image: 'https://images.unsplash.com/photo-1724502555871-b4e079027344?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBjb2xsZWdlJTIwYnVpbGRpbmclMjBjYW1wdXMlMjBDYW5hZGF8ZW58MHx8fHwxNzc3NTkxMzk1fDA&ixlib=rb-4.1.0&q=85',
    programs: [
      {
        name: 'Business Administration',
        duration: '2 years',
        tuition: 'Domestic: $7,500/year | International: $14,000/year',
        description: 'Comprehensive business education covering management, marketing, finance, and entrepreneurship',
        careers: ['Business Manager', 'Operations Manager', 'Marketing Coordinator']
      },
      {
        name: 'Psychology',
        duration: '2 years',
        tuition: 'Domestic: $7,000/year | International: $13,500/year',
        description: 'Study human behavior, mental processes, and psychological research methods',
        careers: ['Psychology Researcher', 'Mental Health Worker', 'Behavioral Analyst']
      },
      {
        name: 'Nutrition & Food Service Management',
        duration: '2 years',
        tuition: 'Domestic: $7,200/year | International: $13,800/year',
        description: 'Learn nutrition science, dietary planning, and food service operations',
        careers: ['Nutritionist', 'Food Service Manager', 'Dietary Consultant']
      },
      {
        name: 'Computer Science',
        duration: '2 years',
        tuition: 'Domestic: $8,000/year | International: $15,000/year',
        description: 'Programming, software development, and computer systems training',
        careers: ['Software Developer', 'IT Specialist', 'Systems Analyst']
      }
    ],
    highlights: [
      'Research-focused curriculum',
      'Experienced faculty with industry expertise',
      'Flexible learning schedules',
      'Career-oriented training programs',
      'Modern laboratory facilities',
      'Strong focus on practical skills'
    ]
  },
  'avalon-community-college': {
    name: 'Avalon Community College',
    location: 'Surrey, BC',
    established: '2010',
    campuses: [
      '13402-104 Avenue, 2nd Floor, Surrey, BC V3T 1V6'
    ],
    description: 'Quality education in Business, Healthcare, Hospitality, and Veterinary fields at Avalon Community College in Surrey, BC.',
    specializations: ['Business', 'Healthcare', 'Hospitality', 'Veterinary'],
    image: 'https://images.unsplash.com/photo-1745558852855-9f9e34017226?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHw0fHx1bml2ZXJzaXR5JTIwY2FtcHVzJTIwQnJpdGlzaCUyMENvbHVtYmlhJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzc3NTkxMzk1fDA&ixlib=rb-4.1.0&q=85',
    programs: [
      {
        name: 'Business Administration Diploma',
        duration: '40 weeks',
        tuition: 'Domestic: $7,000 | International: $13,500',
        description: 'Comprehensive business training covering management, marketing, and operations',
        careers: ['Business Manager', 'Administrative Coordinator', 'Operations Supervisor']
      },
      {
        name: 'Hospitality Management (with Co-op)',
        duration: '60 weeks',
        tuition: 'Domestic: $8,500 | International: $16,000',
        description: 'Hotel management training with valuable co-op work experience',
        careers: ['Hotel Manager', 'Hospitality Coordinator', 'Guest Services Manager']
      },
      {
        name: 'Veterinary Technician Assistant',
        duration: '24 weeks',
        tuition: 'Domestic: $6,500 | International: $12,500',
        description: 'Hands-on training in animal care and veterinary clinic support',
        careers: ['Veterinary Assistant', 'Animal Care Technician', 'Clinic Support Staff']
      },
      {
        name: 'Healthcare Office Administration',
        duration: '40 weeks',
        tuition: 'Domestic: $7,000 | International: $13,000',
        description: 'Medical office procedures and healthcare administration training',
        careers: ['Medical Office Assistant', 'Healthcare Administrator', 'Patient Coordinator']
      },
      {
        name: 'Hospitality Management (without Co-op)',
        duration: '48 weeks',
        tuition: 'Domestic: $7,500 | International: $14,000',
        description: 'Essential hospitality management skills for the tourism industry',
        careers: ['Hotel Supervisor', 'Event Coordinator', 'Front Desk Manager']
      }
    ],
    highlights: [
      'Convenient Surrey location',
      'Hands-on learning approach',
      'Experienced industry professionals as instructors',
      'Career services and job placement assistance',
      'Flexible program options',
      'Modern facilities and equipment'
    ]
  },
  'eton-college': {
    name: 'Eton College',
    location: 'Vancouver, BC',
    established: '2003',
    campuses: [
      'Vancouver Downtown Campus'
    ],
    description: 'Excellence in Travel, Tourism, Hospitality, Business, Flight Attendant, and IT programs since 2003. Located near Vancouver\'s Olympic Village.',
    specializations: ['Aviation', 'Travel & Tourism', 'Hospitality', 'Business', 'IT'],
    image: 'https://images.unsplash.com/photo-1745558858213-c1bb66fc8fde?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHx1bml2ZXJzaXR5JTIwY2FtcHVzJTIwQnJpdGlzaCUyMENvbHVtYmlhJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzc3NTkxMzk1fDA&ixlib=rb-4.1.0&q=85',
    programs: [
      {
        name: 'Flight Attendant Training',
        duration: '26 weeks',
        tuition: 'Domestic: $8,000 | International: $15,000',
        description: 'Comprehensive training for competitive flight attendant roles in a global market',
        careers: ['Flight Attendant', 'Cabin Crew', 'In-Flight Service Professional']
      },
      {
        name: 'Healthcare Office Administration',
        duration: '35 weeks',
        tuition: 'Domestic: $6,800 | International: $13,000',
        description: 'Medical office administration and clinical support training',
        careers: ['Medical Office Assistant', 'Healthcare Administrator', 'Patient Services Coordinator']
      },
      {
        name: 'Hospitality Management',
        duration: '40 weeks',
        tuition: 'Domestic: $7,200 | International: $13,800',
        description: 'Hotel operations, food & beverage, and customer service excellence',
        careers: ['Hotel Manager', 'Hospitality Coordinator', 'Restaurant Manager']
      },
      {
        name: 'Business Administration',
        duration: '48 weeks',
        tuition: 'Domestic: $7,500 | International: $14,200',
        description: 'Comprehensive business skills for management and operations roles',
        careers: ['Business Manager', 'Operations Coordinator', 'Administrative Manager']
      },
      {
        name: 'Airport Operations Management',
        duration: '30 weeks',
        tuition: 'Domestic: $7,800 | International: $14,800',
        description: 'Training in airport management, operations, and ground services',
        careers: ['Airport Operations Manager', 'Ground Operations Coordinator', 'Aviation Supervisor']
      }
    ],
    highlights: [
      'Over 20 years of educational excellence',
      'Partnerships with Trinity Western University',
      'Strategic location near Olympic Village',
      'Industry-relevant curriculum',
      'Career-focused programs',
      'Experienced faculty from aviation and hospitality industries'
    ]
  },
  'multihexa-college': {
    name: 'Multihexa College',
    location: 'Vancouver, BC',
    established: '2004',
    campuses: [
      'Vancouver Campus - Downtown'
    ],
    description: 'Career-focused programs in digital technology and IT with 20+ years of experience. Multihexa is a CompTIA Authorized Partner offering industry-recognized certifications.',
    specializations: ['Web Development', 'Digital Marketing', 'Graphic Design', 'IT & Networking'],
    image: 'https://images.unsplash.com/photo-1635295852796-f6867d868156?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHx1bml2ZXJzaXR5JTIwY2FtcHVzJTIwQnJpdGlzaCUyMENvbHVtYmlhJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzc3NTkxMzk1fDA&ixlib=rb-4.1.0&q=85',
    programs: [
      {
        name: 'Web Development Diploma',
        duration: '52 weeks',
        tuition: 'Domestic: $9,000 | International: $17,000',
        description: 'Full-stack web development training including front-end and back-end technologies',
        careers: ['Web Developer', 'Full Stack Developer', 'Software Engineer']
      },
      {
        name: 'Digital Marketing Certificate',
        duration: '30 weeks',
        tuition: 'Domestic: $7,500 | International: $14,500',
        description: 'Comprehensive digital marketing strategies, SEO, social media, and analytics',
        careers: ['Digital Marketing Specialist', 'Social Media Manager', 'SEO Specialist']
      },
      {
        name: 'Graphic Design Diploma',
        duration: '48 weeks',
        tuition: 'Domestic: $8,500 | International: $16,000',
        description: 'Design principles, Adobe Creative Suite, and UI/UX design',
        careers: ['Graphic Designer', 'UI/UX Designer', 'Visual Designer']
      },
      {
        name: 'Network Administration',
        duration: '40 weeks',
        tuition: 'Domestic: $8,200 | International: $15,500',
        description: 'Network management, Windows servers, and IT support training',
        careers: ['Network Administrator', 'IT Support Specialist', 'Systems Administrator']
      },
      {
        name: 'Business Management Diploma',
        duration: '45 weeks',
        tuition: 'Domestic: $7,800 | International: $14,800',
        description: 'Business operations, project management, and leadership skills',
        careers: ['Business Manager', 'Project Manager', 'Operations Coordinator']
      }
    ],
    highlights: [
      'CompTIA Authorized Partner',
      '90% of graduates started new jobs',
      'Small class sizes for personalized attention',
      'Hands-on practical learning',
      'Industry-recognized certifications',
      'Located in Vancouver tech hub'
    ]
  },
  'taylor-pro-college': {
    name: 'Taylor Pro College',
    location: 'Vancouver, Vernon & Kelowna, BC',
    established: '2010',
    campuses: [
      'Vancouver Campus',
      'Vernon Campus',
      'Kelowna Campus'
    ],
    description: 'Quality academic programs, micro-credentials, and provincially approved driver training programs attuned to market demand. Students are equipped with skills necessary for success.',
    specializations: ['Transportation', 'Business', 'Social Services', 'Driver Training'],
    image: 'https://images.unsplash.com/photo-1724502555871-b4e079027344?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBjb2xsZWdlJTIwYnVpbGRpbmclMjBjYW1wdXMlMjBDYW5hZGF8ZW58MHx8fHwxNzc3NTkxMzk1fDA&ixlib=rb-4.1.0&q=85',
    programs: [
      {
        name: 'Class 1 Driver Training (MELT)',
        duration: '8-12 weeks',
        tuition: 'Domestic: $8,500 | International: $16,000',
        description: 'Mandatory Entry Level Training for commercial truck driving with hands-on experience',
        careers: ['Commercial Truck Driver', 'Transport Driver', 'Long-Haul Trucker']
      },
      {
        name: 'Business Administration Diploma',
        duration: '52 weeks',
        tuition: 'Domestic: $7,500 | International: $14,000',
        description: 'Comprehensive business training for management and administrative roles',
        careers: ['Business Manager', 'Administrative Coordinator', 'Office Manager']
      },
      {
        name: 'Addictions & Community Support Worker',
        duration: '52 weeks',
        tuition: 'Domestic: $8,000 | International: $15,500',
        description: 'Training to support individuals with addiction challenges and community needs',
        careers: ['Addiction Counsellor', 'Community Support Worker', 'Recovery Support Specialist']
      },
      {
        name: 'Social Services Support Worker',
        duration: '52 weeks',
        tuition: 'Domestic: $8,000 | International: $15,500',
        description: 'Prepare for a career supporting individuals and families in social service settings',
        careers: ['Social Service Worker', 'Community Counsellor', 'Family Support Worker']
      },
      {
        name: 'Office Administration Diploma',
        duration: '40 weeks',
        tuition: 'Domestic: $6,800 | International: $13,000',
        description: 'Essential office management, administrative, and organizational skills',
        careers: ['Office Administrator', 'Administrative Assistant', 'Executive Assistant']
      }
    ],
    highlights: [
      'Multiple campus locations across BC',
      'ICBC-approved driver training programs',
      'Experienced instructors with industry backgrounds',
      'Quality academic programs and micro-credentials',
      'Strong partnerships with transportation companies',
      'Career support and job placement assistance'
    ]
  }
};

export const InstitutionDetailPage = () => {
  const { institutionId } = useParams();
  const navigate = useNavigate();
  const institution = institutionData[institutionId];

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [institutionId]);

  if (!institution) {
    return (
      <div className="min-h-screen pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold text-gray-900">Institution Not Found</h1>
          <Link to="/" className="text-blue-600 hover:text-blue-700 mt-4 inline-block">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const contactElement = document.getElementById('contact');
      if (contactElement) {
        contactElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 500);
  };

  const seoSchemas = [
    buildInstitutionSchema({
      name: institution.name,
      slug: institutionId,
      location: institution.location,
      description: institution.description,
      image: institution.image,
    }),
    buildBreadcrumb([
      { name: 'Home', path: '/' },
      { name: 'Partner Institutions', path: '/#partners' },
      { name: institution.name, path: `/institutions/${institutionId}` },
    ]),
  ];

  return (
    <div className="min-h-screen pt-32 pb-16 bg-gray-50">
      <SEO
        title={`${institution.name} – Programs, Tuition & Admissions`}
        description={`${institution.name} in ${institution.location}. ${institution.description.slice(0, 140)}`}
        path={`/institutions/${institutionId}`}
        image={institution.image}
        schema={seoSchemas}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link 
          to="/#partners"
          className="inline-flex items-center text-gray-600 hover:text-blue-600 mb-8 transition-colors duration-200"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Partner Institutions
        </Link>

        {/* Header */}
        <div className="bg-white rounded-2xl overflow-hidden shadow-lg mb-12">
          <div className="h-64 overflow-hidden">
            <img 
              src={institution.image}
              alt={`${institution.name} campus in ${institution.location}`}
              loading="eager"
              decoding="async"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              {institution.name}
            </h1>
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex items-center text-gray-600">
                <MapPin className="w-5 h-5 mr-2 text-blue-600" />
                {institution.location}
              </div>
              <div className="flex items-center text-gray-600">
                <Calendar className="w-5 h-5 mr-2 text-blue-600" />
                Established {institution.established}
              </div>
            </div>
            <p className="text-lg text-gray-700 leading-relaxed">
              {institution.description}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Programs */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Programs Offered ({institution.programs.length})
              </h2>
              <div className="space-y-6">
                {institution.programs.map((program, index) => (
                  <div key={index} className="border-l-4 border-blue-600 pl-6 py-4 hover:bg-gray-50 transition-colors duration-200">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-xl font-bold text-gray-900">
                        {program.name}
                      </h3>
                    </div>
                    <p className="text-gray-600 mb-4">
                      {program.description}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="flex items-center text-sm text-gray-700">
                        <Clock className="w-4 h-4 mr-2 text-blue-600" />
                        <span className="font-medium">{program.duration}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-700">
                        <DollarSign className="w-4 h-4 mr-2 text-green-600" />
                        <span className="font-medium">{program.tuition}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-700">
                        <Award className="w-4 h-4 mr-2 text-purple-600" />
                        <span className="font-medium">Diploma/Certificate</span>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-2 font-medium flex items-center">
                        <TrendingUp className="w-4 h-4 mr-2 text-blue-600" />
                        Career Opportunities:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {program.careers.map((career, idx) => (
                          <span key={idx} className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                            {career}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Campus Locations */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Campus Locations
              </h2>
              <div className="space-y-4">
                {institution.campuses.map((campus, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">{campus}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* CTA Card */}
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 shadow-lg text-white sticky top-24">
              <h3 className="text-2xl font-bold mb-4">
                Interested in {institution.name}?
              </h3>
              <p className="mb-6 opacity-90">
                Book a free consultation to learn more about programs and admission requirements.
              </p>
              <Button 
                onClick={scrollToContact}
                className="w-full bg-white text-blue-600 hover:bg-gray-100"
                size="lg"
              >
                <Calendar className="mr-2 w-5 h-5" />
                Book Free Consultation
              </Button>
            </div>

            {/* Highlights */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <Award className="w-5 h-5 mr-2 text-blue-600" />
                Why Choose {institution.name.split(' ')[0]}?
              </h3>
              <div className="space-y-3">
                {institution.highlights.map((highlight, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 mt-2"></div>
                    <span className="text-gray-700 text-sm">{highlight}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Specializations */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <BookOpen className="w-5 h-5 mr-2 text-blue-600" />
                Specializations
              </h3>
              <div className="flex flex-wrap gap-2">
                {institution.specializations.map((spec, index) => (
                  <span key={index} className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full">
                    {spec}
                  </span>
                ))}
              </div>
            </div>

            {/* Other Institutions */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Other Partner Colleges
              </h3>
              <div className="space-y-2">
                {Object.keys(institutionData)
                  .filter(key => key !== institutionId)
                  .map((key) => (
                    <Link
                      key={key}
                      to={`/institutions/${key}`}
                      className="block text-blue-600 hover:text-blue-700 hover:underline text-sm"
                    >
                      {institutionData[key].name}
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
